import { Modal, ModalProps, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import cls from "classnames/bind";
import { useGlobalStore } from '@/store';
import { createBindQRCode, checkBindResult, createLoginQRCode, checkScanResult } from "@/services/Wechat";
import { sleep } from "@/utils";
import styles from './index.module.scss';
import { CommonResponse } from "@/models/common/response";

const cx = cls.bind(styles);

export interface WechatBindDialogProps extends ModalProps {
  onBindSuccess?: () => void;
}

declare global {
  interface Window {
    currentBindQRCodeKey?: string;
  }
}

const WechatBindDialog = (props: WechatBindDialogProps) => {
  const { open, onCancel, onBindSuccess, ...dialogProps } = props;
  const [qrCode, setQrCode] = useState('');
  const [isBinding, setIsBinding] = useState(false);
  const getUserInfo = useGlobalStore((state) => state.getUserInfo);

  // 检查微信绑定结果
  const loopCheckBindResult = useCallback(async () => {
    if (open && window.currentBindQRCodeKey) {
      try {
        const res: CommonResponse<{ wechatOpenId: string }> = await checkScanResult(window.currentBindQRCodeKey);
        
        if (res.success && res.data?.wechatOpenId) {
          // 绑定成功
          setIsBinding(false);
          await getUserInfo(); // 刷新用户信息
          message.success('微信绑定成功');
          onBindSuccess?.();
          handleClose();
        } else if (!open) {
          // 如果弹窗已关闭，停止轮询
          return;
        } else {
          // 继续轮询
          await sleep(2000);
          await loopCheckBindResult();
        }
      } catch (error) {
        console.error('检查微信绑定结果出错:', error);
        setIsBinding(false);
        message.error('绑定出错，请重试');
      }
    }
  }, [getUserInfo, onBindSuccess, open]);

  // 获取绑定二维码
  useEffect(() => {
    async function fetchBindQRCode() {
      try {
        setIsBinding(true);
        const res: CommonResponse<{ key: string; qrCodeUrl: string }> = await createLoginQRCode();
        if (res.success) {
          const { key, qrCodeUrl } = res.data || {};
          if (qrCodeUrl) {
            setQrCode(qrCodeUrl);
          }
          if (key) {
            window.currentBindQRCodeKey = key;
            loopCheckBindResult();
          }
        } else {
          message.error('获取绑定二维码失败');
          setIsBinding(false);
        }
      } catch (error) {
        console.error('获取绑定二维码出错:', error);
        message.error('获取绑定二维码失败');
        setIsBinding(false);
      }
    }

    if (open) {
      fetchBindQRCode();
    }
  }, [open, loopCheckBindResult]);

  // 关闭弹窗
  const handleClose = () => {
    window.currentBindQRCodeKey = '';
    setQrCode('');
    setIsBinding(false);
    onCancel?.(null as any);
  };

  return (
    <Modal
      title="绑定微信账号"
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={350}
      className={cx('wechatBindDialog')}
      {...dialogProps}
    >
      <div className={cx('content')}>
        {isBinding ? (
          <>
            <div className={cx('qrcodeWrapper')}>
              {qrCode ? (
                <img src={qrCode} alt="微信绑定二维码" />
              ) : (
                <div className={cx('loading')}>获取二维码中...</div>
              )}
            </div>
            <div className={cx('tip')}>请使用微信扫描二维码，完成账号绑定</div>
            <div className={cx('subTip')}>
              绑定成功后，您可以在微信环境内直接使用JSAPI支付
            </div>
          </>
        ) : (
          <div className={cx('loadingError')}>
            获取绑定二维码失败，请关闭后重试
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WechatBindDialog; 